import { TIME_ZONES } from '@drayalliance/types';
import { Role } from '../roles/role.model';

interface Address {
  city: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  country: string;
  state: string;
  street1: string;
  street2: string | null;
  zip: string;
}

export const DepartmentTypes = {
  Admin: 'admin',
  Carrier: 'carrier',
  ChassisPool: 'chassis_pool',
  ChassisYard: 'chassis_yard',
  Consignee: 'consignee',
  ContainerDepot: 'container_depot',
  Customer: 'customer',
  RailYard: 'rail_yard',
  ShippingLine: 'shipping_line',
  Terminal: 'terminal',
  Yard: 'yard',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DepartmentTypes =
  (typeof DepartmentTypes)[keyof typeof DepartmentTypes];

export interface DeliveryPreference<T extends 'import' | 'export'> {
  uuid: string;
  transitTime: number | null;
  terminalTurnTime: number | null;
  preferredDeliveryMethod: string | null;
  deliveryAppointmentType: string | null;
  referenceNumberRequired: string | null;
  appointmentScheduleMethod: string | null;
  mondayHourOpen: string | null;
  mondayHourClose: string | null;
  tuesdayHourOpen: string | null;
  tuesdayHourClose: string | null;
  wednesdayHourOpen: string | null;
  wednesdayHourClose: string | null;
  thursdayHourOpen: string | null;
  thursdayHourClose: string | null;
  fridayHourOpen: string | null;
  fridayHourClose: string | null;
  saturdayHourOpen: string | null;
  saturdayHourClose: string | null;
  sundayHourOpen: string | null;
  sundayHourClose: string | null;
  createdAt: string;
  updatedAt: string;
  type: T;
  departmentUUID: string;
  apptNeeded: boolean | null;
  openTwentyFourHours: boolean | null;
  autoCutPrepull: boolean; // Nullable in the DB, but defaults to false,
  sendApptOutreachInDOThread: boolean;
  apptOutreachEnabled: boolean;
  sameWeekdayHours: boolean;
}

export default interface Department {
  uuid: string;
  name: string;
  nickname: string | null;
  type: DepartmentTypes;
  verified: boolean;
  suspendedUntil: string | null;
  billingAddress: Address | null;
  shippingAddress: Address | null;
  companyUUID: string | null;
  regionUUID: string | null;
  copperOpportunityID: string | null;
  drayageChargeOverride: number | null;
  fscChargeOverride: number | null;
  priceOverrideNote: string | null;
  qbCompanyId: string | null;
  importIngateOptionUUID: string | null;
  importOutgateOptionUUID: string | null;
  exportIngateOptionUUID: string | null;
  exportOutgateOptionUUID: string | null;
  tosVersionDate: string | null;
  opsInstructions: string | null;
  deliveryInstructions: string | null;
  pickupInstructions: string | null;
  mapResponse: Record<string, any> | string;
  vanityUrl: string | null;
  deliveryAutoChange: number;
  warehouseDriverPODNotificationTimeInSecs: number;
  warehouseDriverExitGracePeriodTimeInSecs: number;
  appointmentResetGracePeriodInSecs: number;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  contactNumber: string | null;
  shipperType: string | null;
  estimatedImportVolume: number | null;
  timezone: TIME_ZONES;
  badgeColor: string | null;
  badgeName: string | null;
  importDeliveryPreference?: DeliveryPreference<'import'> | null;
  exportDeliveryPreference?: DeliveryPreference<'export'> | null;
}

export interface DepartmentWithRole extends Department {
  role: Role | null;
}
